import * as React from 'react'
import NextLink from 'next/link'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as cold from '@owl-nest/cold-data/client'
import * as hooks from '@owl-nest/hooks'
import { useHierarchyHref } from '@boutique/hooks'

type MobileHierarchy = {
  tree: typeof cold.hierarchy.tree
}

export function MobileHierarchy({ tree }: MobileHierarchy): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  plume.hooks.useScrollLock(open)

  const [stack, setStack] = React.useState([
    ...tree.parents
      .slice()
      .reverse()
      .map((entry) => entry.id),
    tree.id,
  ])
  const current = stack[stack.length - 1]
  const currentTree = cold.getTree(cold.hierarchy, current)
  const currentHref = useHierarchyHref(currentTree.id)

  const router = hooks.useNextRouter()

  React.useEffect(() => {
    setOpen(false)
  }, [router.asPath])

  return (
    <MobileWrapper>
      <plume.Button color="white" onClick={() => setOpen(true)}>
        <plume.glyphs.stroke.Stack3dLightweight size={20} />
        <plume.styles.heading.XXXXS>Catégories</plume.styles.heading.XXXXS>
      </plume.Button>
      {open && (
        <Overlay>
          <Header>
            <plume.styles.heading.XS>Catégories</plume.styles.heading.XS>
            <CloseButton onClick={() => setOpen(false)}>
              <plume.glyphs.stroke.Close size={16} />
            </CloseButton>
          </Header>
          {currentTree.type !== '$$root$$' && (
            <Back onClick={() => setStack((stack) => stack.slice(0, -1))}>
              <plume.glyphs.stroke.CaretLeftLightweight size={16} />
              <plume.styles.copy.L as="span">{currentTree.name}</plume.styles.copy.L>
            </Back>
          )}
          <List>
            {currentTree.type !== '$$root$$' && (
              <SeeAll>
                <SeeAllLink href={currentHref}>
                  <plume.styles.copy.S as="span">Voir tous les produits {currentTree.name}</plume.styles.copy.S>{' '}
                  <plume.glyphs.stroke.ArrowRight size={14} />
                </SeeAllLink>
              </SeeAll>
            )}
            {currentTree.children.map((child) => {
              return (
                <ListItem key={child.id}>
                  <HierarchyEntry slug={child.id} onNavigate={() => setStack((stack) => [...stack, child.id])}>
                    {child.name}
                  </HierarchyEntry>
                </ListItem>
              )
            })}
          </List>
        </Overlay>
      )}
    </MobileWrapper>
  )
}

const CloseButton = styled.button`
  background: transparent;
  border: 0;
  height: 36px;
  position: absolute;
  right: 10px;
  top: 9px;
  width: 36px;
`

const ListItem = styled.li`
  border-bottom: 1px solid ${plume.COLORS.GREY_SHADE_5};

  &:last-child {
    border-bottom: none;
  }
`

const SeeAll = styled(ListItem)`
  ${plume.styles.copy.S} {
    font-weight: 600;
    margin-right: 8px;
  }
`

const SeeAllLink = styled(NextLink)`
  align-items: center;
  display: flex;
  padding: 16px 0;
`

const List = styled.ul`
  padding: 0 20px;
`

const Back = styled.button`
  padding: 10px 20px 14px;
  background-color: ${plume.COLORS.BLUE_SHADE_3};
  display: flex;
  align-items: center;
  border: none;
  width: 100%;

  ${plume.styles.copy.L} {
    font-weight: 600;
  }

  ${plume.glyphs.stroke.CaretLeftLightweight} {
    margin: 2px 8px 0 0;
  }
`

const MobileWrapper = styled.div`
  display: block;
  margin-bottom: -12px;
  margin-top: 20px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: none;
  }

  ${plume.Button} {
    position: relative;
    width: 100%;
    align-items: center;
    background-color: ${plume.COLORS.PRIMARY_WHITE};
    border: 2px solid ${plume.COLORS.GREY_SHADE_5};
    border-radius: 2;

    ${plume.styles.button.Label} {
      ${plume.styles.heading.XXXXS} {
        color: ${plume.COLORS.GREY_SHADE_1};
      }
      display: flex;
      align-items: center;
    }

    ${plume.glyphs.stroke.Stack3dLightweight} {
      margin: 0 6px -4px 0;
      color: ${plume.COLORS.GREY_SHADE_7};
      fill: none;
      stroke: currentColor;
    }
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 19px;
`

const Overlay = styled.div`
  position: relative;

  @media screen and not ${plume.BREAKPOINTS.TABLET} {
    background-color: ${plume.COLORS.PRIMARY_WHITE};
    bottom: 0;
    flex-direction: column;
    left: 0;
    position: fixed;
    right: 0;
    top: ${plume.SIZES.HEADER_HEIGHT};
    width: 100%;
    z-index: 251;
    overflow: scroll;
  }
`

type EntryProps = {
  slug: string
  className?: string
  onNavigate?: () => void
  children: React.ReactNode
}

function HierarchyEntry({ slug, children, className, onNavigate }: EntryProps): React.ReactElement {
  const url = useHierarchyHref(slug)

  if (isCategory(slug)) {
    return (
      <HierarchyButton isCategory={true} onClick={onNavigate}>
        {children}
      </HierarchyButton>
    )
  }

  if (isProductType(slug)) {
    return (
      <NextLink className={className} href={url}>
        {children}
      </NextLink>
    )
  }

  return (
    <HierarchyButton isCategory={isCategory(slug)} onClick={onNavigate}>
      {children}
    </HierarchyButton>
  )
}

const HierarchyButton = styled.button<{ isCategory: boolean }>`
  border: none;
  cursor: pointer;
  line-height: 22px;
  padding: 16px 4px;
  text-align: left;
  width: 100%;

  ${({ isCategory }) => {
    if (isCategory) {
      return css`
        text-transform: uppercase;
        font-weight: 600;
      `
    }
  }}
`

function isProductType(slug: string) {
  return cold.types['product-type'].includes(slug)
}

function isCategory(slug: string) {
  return cold.types['category'].includes(slug)
}
